/**
 * UnmappedTestsChip.tsx
 * G-4 (scheduling audit 2026-07-01) — sticky "N not schedulable" warning chip.
 * Phase 3 (quote-wo-schedule-lifecycle 2026-07-11) — the chip is now
 * CLICKABLE: it opens a popover panel naming each unmapped test with a
 * plain-language explanation (and, for admins, a deep link to the Test
 * Mapping admin page), plus a milder second section for tests scheduled via
 * an UNCONFIRMED mapping estimate (D4).
 *
 * Shows, beside Phase 1's "N unplaced" chip, how many of the source's tests
 * have NO catalog mapping — generation skipped them entirely, so they are
 * missing from the plan (the PR #280 Direct-Shear-drop failure class). Unlike
 * the 2.5-s success flash / snackbar this chip is STICKY: it recomputes from
 * the durable GET /schedule/unmapped-tests endpoint on every refreshKey bump
 * and never auto-dismisses.
 *
 * Best-effort: a fetch failure hides the chip (advisory only, never blocks).
 * View-model logic lives in unmappedTestsPanelModel.ts (pure, unit-tested).
 */

import React, { useEffect, useState } from 'react';
import {
  Chip, Box, Popover, Typography, Link as MuiLink, Divider, useTheme, alpha,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { fetchUnmappedTests } from '../../../../services/scheduleTaskGeneration';
import { usePermissions } from '../../../../hooks/usePermissions';
import { ROUTES } from '../../../../constants/routes';
import { buildUnmappedTestsPanelModel } from './unmappedTestsPanelModel';

interface UnmappedTestsChipProps {
  source: 'Quote' | 'WorkOrder';
  sourceKey: string;
  /** Bump to re-check (Phase1Panel bumps gridRefreshKey on every mutation/replan). */
  refreshKey: number;
}

export const UnmappedTestsChip: React.FC<UnmappedTestsChipProps> = ({
  source,
  sourceKey,
  refreshKey,
}) => {
  const theme = useTheme();
  const { isAdmin } = usePermissions();
  const [unmapped, setUnmapped] = useState<string[]>([]);
  const [unconfirmed, setUnconfirmed] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchUnmappedTests(source, sourceKey)
      .then((health) => {
        if (cancelled) return;
        setUnmapped(health.unmappedTests);
        setUnconfirmed(health.unconfirmedTests);
      })
      .catch(() => {
        // Best-effort advisory — hide on error rather than alarm falsely.
        if (cancelled) return;
        setUnmapped([]);
        setUnconfirmed([]);
      });
    return () => {
      cancelled = true;
    };
  }, [source, sourceKey, refreshKey]);

  const model = buildUnmappedTestsPanelModel(unmapped, unconfirmed, isAdmin);
  if (!model.chipLabel) return null;

  // Loud (unmapped present) = soft-conflict orange; unconfirmed-only = amber.
  const chipColor = model.chipIsLoud
    ? theme.schedule.conflictSoft
    : theme.schedule.maintenance;

  return (
    <>
      <Chip
        label={model.chipLabel}
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          backgroundColor: alpha(chipColor, 0.12),
          color: chipColor,
          fontSize: '0.72rem',
          cursor: 'pointer',
        }}
      />
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, maxWidth: 380 }}>
          {model.unmapped.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ color: theme.schedule.conflictSoft, fontWeight: 700 }}>
                Not schedulable
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                {model.unmappedExplanation}
              </Typography>
              <Box component="ul" sx={{ my: 1, pl: 2.5 }}>
                {model.unmapped.map((name) => (
                  <Typography key={name} component="li" variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                    {name}
                  </Typography>
                ))}
              </Box>
            </>
          )}

          {model.unconfirmedNote && (
            <>
              {model.unmapped.length > 0 && <Divider sx={{ my: 1 }} />}
              <Typography variant="subtitle2" sx={{ color: theme.schedule.maintenance, fontWeight: 700 }}>
                Unconfirmed mapping
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                {model.unconfirmedNote}
              </Typography>
              <Box component="ul" sx={{ my: 1, pl: 2.5 }}>
                {model.unconfirmed.map((name) => (
                  <Typography key={name} component="li" variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {name}
                  </Typography>
                ))}
              </Box>
            </>
          )}

          {model.showAdminLink && (
            <MuiLink
              component={RouterLink}
              to={ROUTES.ADMIN_TEST_MAPPING}
              variant="body2"
              sx={{ fontSize: '0.8rem', fontWeight: 600 }}
            >
              Fix mappings in Admin → Test Mapping
            </MuiLink>
          )}
          {model.contactAdminNote && (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
              {model.contactAdminNote}
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default UnmappedTestsChip;
